<template>
    <kgr-loader v-if="loading" />
    <kgr-form-scaffold
        v-else
        :with-back-button="false"
        :pending="pending"
        @primaryBtnClicked="update"
    >
        <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
            {{ $t('offering.settings.showInFrontends.headline') }}
        </h2>
        <div class="kgr-row">
            {{ $t('offering.settings.showInFrontends.intro') }}
        </div>

        <div
            v-if="clubOfferingSettings"
            class="kgr-inputGroup"
        >
            <kgr-checkbox
                v-model="clubOfferingSettings.showInFrontends"
                :label="$t('offering.settings.showInFrontends.label')"
            />
        </div>

        <hr class="kgr-separator" />

        <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
            {{ $t('offering.settings.headline') }}
        </h2>
        <div class="kgr-row">
            {{ $t('offering.settings.intro') }}
        </div>

        <kgr-form-row
            :label="$t('offering.settings.urlParticipationConditions')"
            :error="error"
            field-name="conditionsOfParticipationUrl"
        >
            <input
                v-if="clubOfferingSettings"
                v-model.trim="clubOfferingSettings.conditionsOfParticipationUrl"
                type="text"
                class="kgr-input"
                @keyup.enter="update"
            />
        </kgr-form-row>

        <hr class="kgr-separator" />

        <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
            {{ $t('offering.settings.clubFeeCategories.headline') }}
        </h2>
        <div class="kgr-row">
            {{ $t('offering.settings.clubFeeCategories.intro') }}<br />
            <strong class="kgr-text--bold">{{ $t('offering.settings.showInFrontends.hint') }}</strong>
        </div>

        <div
            v-if="clubOfferingSettings"
            class="kgr-inputGroup"
        >
            <kgr-multi-select
                v-if="clubOfferingSettings"
                v-model="selectedClubFeeCategories"
                :options="clubFeeCategoriesOptions()"
                :multiple="true"
                mode="tags"
                @post-input="updateClubFeeCategories()"
            />
        </div>

        <hr class="kgr-separator" />

        <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
            {{ $t('offering.settings.notificationPeriod.headline') }}
        </h2>
        <div class="kgr-row">
            {{ $t('offering.settings.notificationPeriod.intro') }}
        </div>
        <kgr-time-range-picker
            v-if="clubOfferingSettings"
            :start="clubOfferingSettings.notificationPeriod.start"
            :end="clubOfferingSettings.notificationPeriod.end"
            :label="$t('offering.settings.notificationPeriod.label')"
            @change="updateNotificationRange($event)"
            @errorStateChanged="updateNotificationRangeErrorState($event)"
        />

        <hr class="kgr-separator" />

        <h2 class="kgr-headline kgr-headline--h2 kgr-headline--text">
            {{ $t('offering.settings.instructorNameVisibility.headline') }}
        </h2>
        <div class="kgr-row">
            {{ $t('offering.settings.instructorNameVisibility.info') }}
        </div>
        <kgr-instructor-name-visibility
            v-if="clubOfferingSettings"
            :visibility="clubOfferingSettings.instructorNameVisibility"
            :error="error"
            @post-input="updateInstructorNameVisibility"
        />
    </kgr-form-scaffold>
</template>

<script>
import Checkbox from '../../components/common/form/checkbox/Checkbox.vue';
import ClubOfferingSettingsRepository from '../../js/repository/ClubOfferingSettingsRepository.js';
import Error from '../../js/model/Error.js';
import FormRow from '../../components/common/form/FormRow.vue';
import FormScaffold from '../../components/common/form/FormScaffold.vue';
import InstructorNameVisibility from '../../components/offering/InstructorNameVisibility.vue';
import Loading from '../../components/common/Loading.vue';
import MultiSelect from '../../components/common/form/select/MultiSelect.vue';
import TimeRangePicker from '../../components/common/form/picker/TimeRangePicker.vue';

export default {
    name: 'OfferingSettingsOverview',
    components: {
        'kgr-checkbox': Checkbox,
        'kgr-form-row': FormRow,
        'kgr-form-scaffold': FormScaffold,
        'kgr-loader': Loading,
        'kgr-instructor-name-visibility': InstructorNameVisibility,
        'kgr-multi-select': MultiSelect,
        'kgr-time-range-picker': TimeRangePicker,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            pending: false,
            clubOfferingSettings: null,
            error: new Error(),
            selectedClubFeeCategories: this.convertSelectedClubFeeCategories(),
            hasNotificationRangeError: false,
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            ClubOfferingSettingsRepository.findByClub(this.clubId)
                .then((data) => {
                    this.clubOfferingSettings = data;
                    this.selectedClubFeeCategories = this.convertSelectedClubFeeCategories();
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        update() {
            if (this.hasNotificationRangeError) {
                this.$notify.error('Der Endzeitpunkt muss nach dem Startzeitpunkt liegen.');
                return;
            }
            if (!this.clubOfferingSettings.isValidInstructorNameVisibility()) {
                if (this.error.messagesForField('instructorNameVisibility') === null) {
                    this.error.add('instructorNameVisibility', this.$t('offering.settings.error.invalidValue'));
                }
                return;
            }
            this.pending = true;
            ClubOfferingSettingsRepository.update(this.clubId, this.clubOfferingSettings)
                .then(() => {
                    this.error = new Error();
                    this.$notify.success('saveChanges.success');
                })
                .catch((error) => {
                    this.error = error;
                    this.showError(error);
                })
                .finally(() => this.pending = false);
        },
        updateNotificationRange(range) {
            this.clubOfferingSettings.notificationPeriod.start = range.start.toString();
            this.clubOfferingSettings.notificationPeriod.end = range.end.toString();
        },
        updateNotificationRangeErrorState(error) {
            this.hasNotificationRangeError = error;
        },
        showError(error) {
            const msg = error.details[0].constraintMessage;
            if (msg) {
                this.$notify.error(msg);
            } else {
                this.$notify.error('saveChanges.error');
            }
        },
        clubFeeCategoriesOptions() {
            if (!this.clubOfferingSettings) {
                return [];
            }
            const clubFeeCategoriesOptions = this.clubOfferingSettings.clubFeeCategories.map((clubFeeCategory) => {
                return { value: clubFeeCategory.id, label: clubFeeCategory.name, isSelectableInOfferings: clubFeeCategory.isSelectableInOfferings };
            });
            return clubFeeCategoriesOptions.filter((clubFeeCategory) => !clubFeeCategory.isSelectableInOfferings);
        },
        convertSelectedClubFeeCategories() {
            if (!this.clubOfferingSettings) {
                return [];
            }
            const clubFeeCategoriesOptions = this.clubOfferingSettings.clubFeeCategories.map((clubFeeCategory) => {
                return { value: clubFeeCategory.id, label: clubFeeCategory.name, isSelectableInOfferings: clubFeeCategory.isSelectableInOfferings };
            });
            return clubFeeCategoriesOptions.filter((clubFeeCategory) => clubFeeCategory.isSelectableInOfferings);
        },
        updateClubFeeCategories() {
            this.clubOfferingSettings.clubFeeCategories.forEach((clubFeeCategory) => clubFeeCategory.isSelectableInOfferings = false);
            this.selectedClubFeeCategories.forEach((clubFeeCategory) => this.setClubFeeCategoryIsSelectableIfOfferings(clubFeeCategory));
        },
        setClubFeeCategoryIsSelectableIfOfferings(clubFeeCategory) {
            this.clubOfferingSettings.clubFeeCategories.forEach((item) => {
                if (item.id === clubFeeCategory.value) {
                    item.isSelectableInOfferings = true;
                }
            });
        },
        updateInstructorNameVisibility(value) {
            this.clubOfferingSettings.instructorNameVisibility = value;
        },
    },
};
</script>
